import { useCallback, useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useLocation, useNavigate } from "react-router";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { invocarFuncao } from "@/lib/edgeFunction";

/**
 * Conta Google do usuário (migration 302, edge function google-oauth): enviar
 * e-mail pelo Gmail dele e usar a Agenda dele. Nunca lê e-mail (decisão do
 * Rick, 29/10/2026: sem gmail.readonly, sem auditoria).
 *
 * Conectar sai do app para a tela de consentimento do Google e volta para
 * Meu perfil com ?google=<resultado>; `useAvisoDoRetornoGoogle` transforma esse
 * resultado em aviso e limpa o endereço.
 */

export type LinhaGoogle = { email: string; situacao: "ok" | "revogado"; conectado_em: string };

export const resumoGoogle = (l: LinhaGoogle | null | undefined) => {
  if (!l) return { estado: "nao_conectado" as const, detalhe: "Enviar e-mail e agendar pela conta Google." };
  if (l.situacao === "revogado") return { estado: "incompleto" as const, detalhe: `${l.email} · acesso expirado, conectar de novo.` };
  return { estado: "conectado" as const, detalhe: `${l.email} · e-mail e agenda` };
};

/** A linha do usuário (RLS: a própria; super admin vê todas). */
export const useConexaoGoogle = (crmUserId: number | string | null | undefined) => {
  const [linha, setLinha] = useState<LinhaGoogle | null>(null);
  const recarregar = useCallback(async () => {
    if (crmUserId == null) return null;
    const { data } = await supabase
      .from("google_conexoes" as never)
      .select("email, situacao, conectado_em")
      .eq("crm_user_id", Number(crmUserId))
      .maybeSingle();
    const l = (data ?? null) as LinhaGoogle | null;
    setLinha(l);
    return l;
  }, [crmUserId]);
  useEffect(() => {
    void recarregar();
  }, [recarregar]);
  return { linha, recarregar };
};

/** Vai para a tela de consentimento do Google. */
export const conectarGoogle = async (): Promise<string | null> => {
  const { dados, erro } = await invocarFuncao<{ url: string }>("google-oauth", {
    body: { acao: "iniciar" },
    quando: "conectar a conta Google",
  });
  if (erro || !dados?.url) return erro ?? "Não foi possível conectar a conta Google.";
  window.location.assign(dados.url);
  return null;
};

export const desconectarGoogle = async () => {
  const { erro } = await invocarFuncao<{ ok: boolean }>("google-oauth", {
    body: { acao: "desconectar" },
    quando: "desconectar a conta Google",
  });
  if (erro) throw new Error(erro);
};

const AVISOS: Record<string, { texto: string; tipo: "info" | "warning" | "error" }> = {
  conectado: { texto: "Conta Google conectada.", tipo: "info" },
  cancelado: { texto: "Conexão com o Google cancelada.", tipo: "warning" },
  permissoes: {
    texto: "Conexão não concluída: as permissões de enviar e-mail e de agenda são necessárias.",
    tipo: "warning",
  },
  expirado: { texto: "Link de conexão expirado. Conectar de novo.", tipo: "warning" },
  erro: { texto: "Não foi possível conectar a conta Google.", tipo: "error" },
};

/** Lê ?google= da volta do Google, avisa uma vez e limpa o endereço. */
export const useAvisoDoRetornoGoogle = (aoVoltar?: () => void) => {
  const local = useLocation();
  const navegar = useNavigate();
  const notify = useNotify();
  useEffect(() => {
    const p = new URLSearchParams(local.search);
    const resultado = p.get("google");
    if (!resultado) return;
    const aviso = AVISOS[resultado] ?? AVISOS.erro;
    notify(aviso.texto, { type: aviso.tipo });
    p.delete("google");
    navegar({ pathname: local.pathname, search: p.toString() }, { replace: true });
    aoVoltar?.();
    // Só na chegada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.search]);
};

/** O cartão do Meu perfil: estado, conectar (sai para o Google) e desconectar. */
export const useAcoesGoogle = (crmUserId: number | string | null | undefined) => {
  const notify = useNotify();
  const google = useConexaoGoogle(crmUserId);
  const [indoAoGoogle, setIndoAoGoogle] = useState(false);
  useAvisoDoRetornoGoogle(() => void google.recarregar());

  const irAoGoogle = async () => {
    setIndoAoGoogle(true);
    const erro = await conectarGoogle();
    if (erro) {
      setIndoAoGoogle(false);
      notify(erro, { type: "error" });
    }
  };

  const desconectarDoGoogle = async () => {
    try {
      await desconectarGoogle();
      notify("Conta Google desconectada.");
      await google.recarregar();
    } catch {
      notify("Não foi possível desconectar a conta Google.", { type: "error" });
    }
  };

  return { google, indoAoGoogle, irAoGoogle, desconectarDoGoogle };
};

// ── Ações usadas fora do Meu perfil (ficha do contato, tarefas) ─────────────

export const enviarEmailPeloGoogle = (corpo: { contato_id?: number | string | null; para: string; assunto: string; texto: string }) =>
  invocarFuncao<{ ok: boolean; de: string }>("google-oauth", {
    body: { acao: "enviar_email", ...corpo },
    quando: "enviar o e-mail pelo Gmail",
  });

export const agendarReuniaoPeloGoogle = (corpo: {
  contato_id: number | string;
  titulo: string;
  inicio: string;
  duracao_min: number;
  descricao?: string;
  convidar: boolean;
  com_meet: boolean;
}) =>
  invocarFuncao<{ ok: boolean; link: string | null; meet: string | null }>("google-oauth", {
    body: { acao: "agendar_reuniao", ...corpo },
    quando: "agendar a reunião na Agenda Google",
  });

/**
 * Leva a tarefa para a Agenda Google do dono (cria, move ou tira o evento).
 * Silencioso: sem Google conectado a função responde "ignorado", e falha aqui
 * não pode atrapalhar a tarefa, que já foi gravada no CRM.
 */
export const sincronizarTarefaNoGoogle = (
  tarefaId: number | string,
  opcoes: { googleEventId?: string | null; apagar?: boolean } = {},
) =>
  invocarFuncao<{ ok: boolean }>("google-oauth", {
    body: {
      acao: "sincronizar_tarefa",
      tarefa_id: tarefaId,
      google_event_id: opcoes.googleEventId ?? null,
      apagar: opcoes.apagar === true,
    },
    quando: "atualizar a Agenda Google",
  }).catch(() => undefined);
